/**
 * Barra de acciones de un episodio — F2.d, checklist de paridad nº1, 2 y 4.
 *
 * Son las tres cosas que Ops hace HOY A MANO fuera del panel: reenviar una
 * plantilla, tener el link de pago delante y generar el OTP del área de
 * cliente. Las tres viven aquí y no en `EpisodeDetail` para que la ficha siga
 * siendo legible: el detalle es una pantalla de LECTURA con una barra de
 * acciones encima, no un formulario gigante.
 *
 * Las dos reglas que gobiernan esta UI:
 *
 *   1. **`simulated: true` no es un error.** Cuando el gate del mercado está
 *      OFF, ms-ops responde 200 sin enviar nada. Si la UI pintara eso como un
 *      éxito normal, Ops creería haber escrito al cliente. Se avisa en verde
 *      grisáceo y con las palabras "no se ha enviado nada".
 *
 *   2. **Un 503 no es culpa del operador.** Significa que el canal (o KITT, o
 *      ms-payment) no está configurado en el entorno. Se deshabilita el botón
 *      con un aviso discreto en vez de dejar que Ops lo pulse en bucle.
 */

import { useCallback, useMemo, useState } from "react";
import {
  getEpisodeOtpLink,
  getEpisodePaymentLinks,
  registerExternalPayment,
  resumeSubscription,
  sendEpisodeMessage,
  setCollectionFee,
} from "../../../api/msops";
import {
  availableTemplatesFromError,
  CHANNEL_LABEL,
  EpisodeDetail as EpisodeDetailType,
  InvoicePaymentLink,
  MANUAL_SEND_CHANNELS,
  ManualSendChannel,
  ManualSendResponse,
  msOpsErrorBody,
  MSOPS_ACTION_ERROR_HINT,
  OtpLinkResponse,
} from "../../../types/msops";
import {
  Badge,
  buttonStyle,
  COLORS,
  Card,
  CopyableValue,
  fieldStyle,
  inputStyle,
  labelStyle,
  secondaryButtonStyle,
} from "./ui";

/**
 * Extrae las plantillas declaradas en el `cadenceSnapshot` del episodio para
 * un canal y el mercado del propio episodio.
 *
 * Es la MISMA lectura que hace el back en `assertTemplateForMarket`, hecha en
 * cliente para poder ofrecer el desplegable ANTES de que Ops se coma un 422.
 * Cuando el 422 llega igualmente (snapshot desincronizado, plantilla retirada)
 * la lista autoritativa del error sustituye a ésta.
 */
export function templatesFromSnapshot(
  snapshot: unknown,
  channel: ManualSendChannel,
  market: string,
): string[] {
  const steps = (snapshot as { steps?: unknown } | null)?.steps;
  if (!Array.isArray(steps)) return [];
  const found = new Set<string>();
  for (const raw of steps) {
    const step = raw as {
      channel?: unknown;
      templateByMarket?: Record<string, unknown>;
    };
    if (step?.channel !== channel) continue;
    const tpl = step.templateByMarket?.[market];
    if (typeof tpl === "string" && tpl !== "") found.add(tpl);
  }
  return [...found].sort();
}

/** Estado de una acción que devuelve un valor copiable (link de pago / OTP). */
type FetchState<T> =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; value: T }
  | { kind: "error"; message: string; hint: string | null; status?: number };

/**
 * Traduce un fallo de acción a {mensaje, pista}. El `message` de ms-ops ya
 * viene en castellano y explica QUÉ pasó; la pista añade el siguiente paso.
 * Si no hay cuerpo de ms-ops (proxy caído, 500 sin JSON) se cae a un texto
 * genérico con el status, que al menos es accionable para Tecnología.
 */
function describeActionError(
  err: unknown,
  fallback: string,
): { message: string; hint: string | null; status?: number } {
  const status = (err as { status?: number } | null)?.status;
  const body = msOpsErrorBody(err);
  if (body) {
    return {
      message: body.message || fallback,
      hint: MSOPS_ACTION_ERROR_HINT[body.error] ?? null,
      status,
    };
  }
  if (status === 404) {
    return {
      message: "Recobros no está disponible en este entorno.",
      hint: null,
      status,
    };
  }
  return {
    message: typeof status === "number" ? `${fallback} (HTTP ${status})` : fallback,
    hint: null,
    status,
  };
}

/** ¿El fallo es "no configurado en este entorno" (503)? */
function isUnconfigured(state: FetchState<unknown>): boolean {
  return state.kind === "error" && state.status === 503;
}

export default function ActionsBar({
  episode,
  token,
  onSent,
}: {
  episode: EpisodeDetailType;
  token: string | null | undefined;
  /** Se llama tras un envío REAL para que la ficha recargue su historial. */
  onSent: () => void;
}) {
  const [sendOpen, setSendOpen] = useState(false);
  const [payment, setPayment] = useState<FetchState<InvoicePaymentLink[]>>({
    kind: "idle",
  });
  const [otp, setOtp] = useState<FetchState<OtpLinkResponse>>({ kind: "idle" });
  const [resume, setResume] = useState<FetchState<{ subscriptionUuid: string }>>({
    kind: "idle",
  });

  /**
   * «Reanudar el cobro». Se pregunta antes porque, aunque en ms-payment es
   * idempotente, para el cliente significa que se le vuelve a pasar el recibo.
   */
  const doResume = useCallback(async () => {
    if (
      !window.confirm(
        "Se reanudará el cobro de esta suscripción y volverán a pasarse los recibos. ¿Seguir?",
      )
    ) {
      return;
    }
    setResume({ kind: "loading" });
    try {
      const value = await resumeSubscription(episode.id, { token });
      setResume({ kind: "ready", value });
      onSent();
    } catch (err) {
      setResume({
        kind: "error",
        ...describeActionError(err, "No se pudo reanudar el cobro"),
      });
    }
  }, [episode.id, token, onSent]);

  const loadPaymentLinks = useCallback(async () => {
    setPayment({ kind: "loading" });
    try {
      const res = await getEpisodePaymentLinks(episode.id, { token });
      setPayment({ kind: "ready", value: res.links });
    } catch (err) {
      setPayment({
        kind: "error",
        ...describeActionError(err, "No se pudieron obtener los links de pago"),
      });
    }
  }, [episode.id, token]);

  const loadOtp = useCallback(async () => {
    setOtp({ kind: "loading" });
    try {
      setOtp({ kind: "ready", value: await getEpisodeOtpLink(episode.id, { token }) });
    } catch (err) {
      setOtp({
        kind: "error",
        ...describeActionError(err, "No se pudo generar el OTP"),
      });
    }
  }, [episode.id, token]);

  // Un episodio cerrado no admite envío manual (el back responde 409): se
  // deshabilita el botón con el motivo delante en vez de dejar que Ops lo
  // descubra al pulsar.
  const closed = episode.status === "closed";

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          onClick={() => setSendOpen((v) => !v)}
          disabled={closed}
          title={
            closed
              ? "El episodio está cerrado: no se comunica sobre una deuda saldada"
              : "Enviar una plantilla a este cliente"
          }
          style={{
            ...buttonStyle,
            opacity: closed ? 0.6 : 1,
            cursor: closed ? "not-allowed" : "pointer",
          }}
        >
          Enviar…
        </button>

        <button
          type="button"
          onClick={loadPaymentLinks}
          disabled={payment.kind === "loading" || isUnconfigured(payment)}
          style={secondaryButtonStyle}
        >
          {payment.kind === "loading" ? "Cargando…" : "Link de pago"}
        </button>

        <button
          type="button"
          onClick={doResume}
          disabled={resume.kind === "loading" || isUnconfigured(resume)}
          title="Deshace una pausa de cobro puesta por error"
          style={secondaryButtonStyle}
        >
          {resume.kind === "loading" ? "Reanudando…" : "Reanudar el cobro"}
        </button>

        <button
          type="button"
          onClick={loadOtp}
          disabled={otp.kind === "loading" || isUnconfigured(otp)}
          style={secondaryButtonStyle}
        >
          {otp.kind === "loading" ? "Cargando…" : "OTP área de cliente"}
        </button>
      </div>

      {sendOpen && (
        <SendPanel
          episode={episode}
          token={token}
          onClose={() => setSendOpen(false)}
          onSent={onSent}
        />
      )}

      {resume.kind === "error" && (
        <ActionCard title="Reanudar el cobro">
          <ActionError
            message={resume.message}
            hint={resume.hint}
            discreet={resume.status === 503}
          />
        </ActionCard>
      )}
      {resume.kind === "ready" && (
        <ActionCard title="Reanudar el cobro">
          <Muted>
            Cobro reanudado. La suscripción vuelve a su ciclo normal; esto no
            cambia nada de la reclamación en curso.
          </Muted>
        </ActionCard>
      )}

      {payment.kind !== "idle" && (
        <ActionCard title="Links de pago">
          {payment.kind === "loading" && <Muted>Consultando ms-payment…</Muted>}
          {payment.kind === "error" && (
            <ActionError
              message={payment.message}
              hint={payment.hint}
              discreet={payment.status === 503}
            />
          )}
          {payment.kind === "ready" &&
            (payment.value.length === 0 ? (
              <Muted>Este episodio no tiene facturas pendientes.</Muted>
            ) : (
              <div style={{ display: "grid", gap: "0.6rem" }}>
                {payment.value.map((link) => (
                  <PaymentLinkRow
                    key={link.invoiceId}
                    link={link}
                    episodeId={episode.id}
                    token={token}
                    onChanged={() => {
                      // Un pago cambia lo que se debe: se refresca la lista y
                      // la ficha, que es donde se ve si el caso ya se cerró.
                      void loadPaymentLinks();
                      onSent();
                    }}
                  />
                ))}
              </div>
            ))}
        </ActionCard>
      )}

      {otp.kind !== "idle" && (
        <ActionCard title="OTP del área de cliente">
          {otp.kind === "loading" && <Muted>Generando enlace…</Muted>}
          {otp.kind === "error" && (
            <ActionError
              message={otp.message}
              hint={otp.hint}
              discreet={otp.status === 503}
            />
          )}
          {otp.kind === "ready" && (
            <div style={{ display: "grid", gap: "0.5rem" }}>
              <CopyableValue label="Enlace" value={otp.value.url} />
              {otp.value.code && (
                <CopyableValue label="Código" value={otp.value.code} />
              )}
            </div>
          )}
        </ActionCard>
      )}
    </>
  );
}

/**
 * Panel de envío manual: canal + plantilla.
 *
 * No hay campo de texto libre y no es un descuido: email va SIEMPRE por
 * `storage-template` del email-orchestrator (saltárselo se salta la
 * comprobación fail-closed de supresiones) y WhatsApp SIEMPRE por plantilla de
 * waserver (un texto libre pone `isBot=false` y mata la automatización de esa
 * conversación para siempre). El contrato no admite `body`, así que la UI
 * tampoco lo ofrece.
 */
function SendPanel({
  episode,
  token,
  onClose,
  onSent,
}: {
  episode: EpisodeDetailType;
  token: string | null | undefined;
  onClose: () => void;
  onSent: () => void;
}) {
  const [channel, setChannel] = useState<ManualSendChannel>("email");
  const [templateId, setTemplateId] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ManualSendResponse | null>(null);
  const [error, setError] = useState<
    { message: string; hint: string | null; status?: number } | null
  >(null);
  // Plantillas que el 422 declaró válidas. Mandan sobre las del snapshot:
  // vienen del back, que es quien decide.
  const [override, setOverride] = useState<string[] | null>(null);

  const snapshotTemplates = useMemo(
    () => templatesFromSnapshot(episode.cadenceSnapshot, channel, episode.market),
    [episode.cadenceSnapshot, channel, episode.market],
  );

  const templates = override ?? snapshotTemplates;

  const submit = async () => {
    if (busy || templateId.trim() === "") return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await sendEpisodeMessage(
        episode.id,
        { channel, templateId: templateId.trim(), dryRun: dryRun || undefined },
        { token },
      );
      setResult(res);
      setOverride(null);
      // Sólo se recarga la ficha si el envío fue REAL: una simulación no ha
      // creado ningún `scheduled_sends` que mostrar.
      if (!res.simulated) onSent();
    } catch (err) {
      const available = availableTemplatesFromError(err);
      if (available.length > 0) setOverride(available);
      setError(describeActionError(err, "No se pudo enviar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      style={{
        padding: "0.75rem 0.95rem",
        marginTop: "0.65rem",
        borderLeft: `3px solid ${COLORS.link}`,
      }}
    >
      <div style={{ marginBottom: "0.5rem" }}>
        <strong style={{ color: COLORS.heading }}>Enviar plantilla</strong>
        <div style={{ fontSize: ".75rem", color: COLORS.muted }}>
          Sólo plantillas: el envío de texto libre no está permitido (email va
          por el orquestador y WhatsApp por plantilla de Meta).
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.75rem",
          alignItems: "flex-end",
        }}
      >
        <label style={fieldStyle}>
          <span style={labelStyle}>Canal</span>
          <select
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value as ManualSendChannel);
              setTemplateId("");
              setOverride(null);
            }}
            style={inputStyle}
          >
            {MANUAL_SEND_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </select>
        </label>

        <label style={{ ...fieldStyle, flex: "1 1 340px" }}>
          <span style={labelStyle}>
            Plantilla {override ? "(válidas según el servidor)" : ""}
          </span>
          {templates.length > 0 ? (
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              style={inputStyle}
            >
              <option value="">— Elige una plantilla —</option>
              {templates.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={templateId}
              placeholder="gs://… o id de plantilla de Meta"
              onChange={(e) => setTemplateId(e.target.value)}
              style={inputStyle}
            />
          )}
        </label>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.35rem",
            fontSize: ".78rem",
            color: COLORS.muted,
          }}
          title="Simula el envío sin mandar nada, para ver qué saldría"
        >
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
          />
          Sólo simular
        </label>

        <button
          type="button"
          onClick={submit}
          disabled={busy || templateId.trim() === ""}
          style={{
            ...buttonStyle,
            opacity: templateId.trim() === "" ? 0.6 : 1,
          }}
        >
          {busy ? "Enviando…" : dryRun ? "Simular" : "Enviar"}
        </button>
        <button type="button" onClick={onClose} style={secondaryButtonStyle}>
          Cerrar
        </button>
      </div>

      {templates.length === 0 && (
        <div
          style={{
            marginTop: "0.5rem",
            fontSize: ".75rem",
            color: COLORS.muted,
          }}
        >
          La cadencia congelada de este episodio no declara plantillas de
          {CHANNEL_LABEL[channel]} para el mercado {episode.market}. Puedes
          escribir el identificador a mano, pero el servidor lo rechazará si no
          está declarado.
        </div>
      )}

      {error && (
        <div style={{ marginTop: "0.6rem" }}>
          <ActionError message={error.message} hint={error.hint} />
        </div>
      )}

      {result && <SendResult result={result} />}
    </Card>
  );
}

/**
 * Resultado de un envío. La distinción simulado / real es lo más importante
 * de esta pantalla: un `simulated: true` pintado como éxito haría creer a Ops
 * que el cliente ya tiene el aviso.
 */
function SendResult({ result }: { result: ManualSendResponse }) {
  const { wouldSend } = result;
  return (
    <div
      style={{
        marginTop: "0.65rem",
        padding: "0.6rem 0.75rem",
        borderRadius: "0.45rem",
        background: result.simulated ? "#e7f1ff" : "#d4edda",
        color: result.simulated ? "#0069d9" : "#155724",
        fontSize: ".82rem",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          flexWrap: "wrap",
        }}
      >
        <Badge
          bg={result.simulated ? "#0069d9" : "#155724"}
          fg="#ffffff"
        >
          {result.simulated ? "Modo shadow" : "Enviado"}
        </Badge>
        <strong>
          {result.simulated
            ? "No se ha enviado nada."
            : "La comunicación ha salido."}
        </strong>
      </div>
      {result.simulated && (
        <div style={{ marginTop: "0.25rem" }}>
          {result.simulatedReason ??
            "El gate de este mercado está cerrado, así que sólo se ha simulado."}
          {" "}
          Abajo tienes exactamente lo que habría salido.
        </div>
      )}

      <div
        style={{
          marginTop: "0.5rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.4rem",
          fontSize: ".78rem",
        }}
      >
        <div>
          <div style={labelStyle}>Canal</div>
          <div>{CHANNEL_LABEL[wouldSend.channel]}</div>
        </div>
        <div>
          <div style={labelStyle}>Destinatario</div>
          <div style={{ fontFamily: "monospace" }}>
            {wouldSend.to ?? "—"}
          </div>
        </div>
        <div>
          <div style={labelStyle}>Mercado</div>
          <div>{wouldSend.market}</div>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={labelStyle}>Plantilla</div>
          <div style={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            {wouldSend.templateId}
          </div>
        </div>
      </div>

      {Object.keys(wouldSend.variables).length > 0 && (
        <details style={{ marginTop: "0.5rem" }}>
          <summary style={{ cursor: "pointer", fontSize: ".78rem" }}>
            Variables de la plantilla
          </summary>
          <pre
            style={{
              margin: "0.35rem 0 0",
              fontSize: ".72rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {JSON.stringify(wouldSend.variables, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

/**
 * Una fila de recibo: el link para cobrarlo y las dos decisiones de dinero que
 * se toman SOBRE ESE recibo (entregas de ms-payment del 2026-08-11).
 *
 * Están aquí y no en la barra del episodio porque ambas son por RECIBO: un
 * cliente con tres impagados puede haber pagado uno por transferencia y deber
 * los otros dos. Colgarlas del episodio obligaría a preguntar "¿cuál?" en un
 * segundo paso, que es exactamente el paso donde se equivoca uno.
 */
function PaymentLinkRow({
  link,
  episodeId,
  token,
  onChanged,
}: {
  link: InvoicePaymentLink;
  episodeId: string;
  token: string | null | undefined;
  /** Tras registrar un pago, la ficha recarga: el episodio puede cerrarse. */
  onChanged: () => void;
}) {
  const [open, setOpen] = useState<null | "fee" | "paid">(null);

  const amount =
    link.amountDueCents !== null
      ? new Intl.NumberFormat("es-ES", {
          style: "currency",
          currency: link.currency || "EUR",
        }).format(link.amountDueCents / 100)
      : "—";

  return (
    <div
      style={{
        padding: "0.5rem 0",
        borderTop: `1px solid ${COLORS.rowBorder}`,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "0.3rem",
          fontSize: ".78rem",
          color: COLORS.muted,
        }}
      >
        <strong style={{ fontFamily: "monospace", color: COLORS.ink }}>
          {link.invoiceId}
        </strong>
        <span>{amount}</span>
        {link.status && <Badge bg="#f8f9fb" fg="#73879C">{link.status}</Badge>}
      </div>
      {link.paymentUrl ? (
        <CopyableValue value={link.paymentUrl} />
      ) : (
        <div style={{ fontSize: ".78rem", color: COLORS.dangerFg }}>
          Sin link: {link.error ?? "motivo desconocido"}
        </div>
      )}

      <div style={{ display: "flex", gap: ".4rem", marginTop: ".45rem", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setOpen((v) => (v === "paid" ? null : "paid"))}
          style={{ ...secondaryButtonStyle, fontSize: ".75rem", padding: ".25rem .55rem" }}
        >
          Ya pagó por fuera…
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => (v === "fee" ? null : "fee"))}
          style={{ ...secondaryButtonStyle, fontSize: ".75rem", padding: ".25rem .55rem" }}
        >
          Gastos de recobro…
        </button>
      </div>

      {open === "paid" && (
        <ExternalPaymentForm
          episodeId={episodeId}
          invoiceId={link.invoiceId}
          suggestedCents={link.amountDueCents}
          token={token}
          onClose={() => setOpen(null)}
          onDone={onChanged}
        />
      )}
      {open === "fee" && (
        <CollectionFeeForm
          episodeId={episodeId}
          invoiceId={link.invoiceId}
          token={token}
          onClose={() => setOpen(null)}
          onDone={onChanged}
        />
      )}
    </div>
  );
}

/**
 * «Ya pagó por fuera» — transferencia o efectivo.
 *
 * El importe viene propuesto con lo que se debe, pero es EDITABLE: los pagos
 * parciales existen y forzar el total convertiría un cobro de 40 € en uno de
 * 120 € que nadie ha recibido.
 *
 * NO cierra el episodio: el pago entra en el libro de ms-payment y es su evento
 * el que cierra el caso. Se avisa en el texto para que Ops no interprete que la
 * ficha está rota si sigue abierta unos segundos.
 */
function ExternalPaymentForm({
  episodeId,
  invoiceId,
  suggestedCents,
  token,
  onClose,
  onDone,
}: {
  episodeId: string;
  invoiceId: string;
  suggestedCents: number | null;
  token: string | null | undefined;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(
    suggestedCents !== null ? (suggestedCents / 100).toFixed(2) : "",
  );
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<"transfer" | "cash" | "other">("transfer");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cents = Math.round(Number(amount.replace(",", ".")) * 100);
  const valid = Number.isFinite(cents) && cents > 0 && /^\d{4}-\d{2}-\d{2}$/.test(paidAt);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await registerExternalPayment(
        episodeId,
        invoiceId,
        {
          amountCents: cents,
          paidAt,
          method,
          reference: reference.trim() || undefined,
        },
        { token },
      );
      onClose();
      onDone();
    } catch (e) {
      setErr(describeActionError(e, "No se pudo registrar el pago").message);
      setBusy(false);
    }
  }

  return (
    <InlineForm>
      <div style={{ fontSize: ".8rem" }}>
        <strong>El cliente ya pagó por otra vía.</strong> Entra en el libro como
        un cobro más. El caso se cierra solo cuando ms-payment confirme el
        apunte, no al pulsar aquí.
      </div>
      <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
        <label style={{ display: "grid", gap: ".25rem" }}>
          <span style={labelStyle}>Importe (€)</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={{ ...inputStyle, width: 110 }}
          />
        </label>
        <label style={{ display: "grid", gap: ".25rem" }}>
          <span style={labelStyle}>Fecha del pago</span>
          <input
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            style={{ ...inputStyle, width: 150 }}
          />
        </label>
        <label style={{ display: "grid", gap: ".25rem" }}>
          <span style={labelStyle}>Vía</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as "transfer" | "cash" | "other")}
            style={{ ...inputStyle, width: 150 }}
          >
            <option value="transfer">Transferencia</option>
            <option value="cash">Efectivo</option>
            <option value="other">Otra</option>
          </select>
        </label>
      </div>
      <label style={{ display: "grid", gap: ".25rem" }}>
        <span style={labelStyle}>Referencia (opcional)</span>
        <input
          type="text"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="p.ej. el concepto del ingreso"
          style={inputStyle}
        />
      </label>
      {err && <ActionError message={err} hint={null} />}
      <FormButtons
        confirmLabel={busy ? "Registrando…" : "Registrar el pago"}
        onConfirm={submit}
        onCancel={onClose}
        disabled={!valid || busy}
      />
    </InlineForm>
  );
}

/**
 * «Gastos de recobro» — repercutir al cliente lo que ha costado su impago.
 *
 * El IMPORTE no se pide: lo pone ms-payment desde el coste real. Exonerar exige
 * motivo por la misma razón que condonar — es dinero que se deja de reclamar.
 */
function CollectionFeeForm({
  episodeId,
  invoiceId,
  token,
  onClose,
  onDone,
}: {
  episodeId: string;
  invoiceId: string;
  token: string | null | undefined;
  onClose: () => void;
  onDone: () => void;
}) {
  const [charge, setCharge] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid = charge || reason.trim().length >= 3;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await setCollectionFee(
        episodeId,
        invoiceId,
        { charge, reason: reason.trim() || undefined },
        { token },
      );
      onClose();
      onDone();
    } catch (e) {
      setErr(describeActionError(e, "No se pudo aplicar la decisión").message);
      setBusy(false);
    }
  }

  return (
    <InlineForm>
      <div style={{ fontSize: ".8rem" }}>
        <strong>¿El cliente paga los gastos de este impago?</strong> El importe
        lo calcula ms-payment con el coste real; aquí solo se decide si se le
        repercuten.
      </div>
      <div style={{ display: "flex", gap: "1rem", fontSize: ".82rem" }}>
        <label style={{ display: "flex", gap: ".3rem", alignItems: "center" }}>
          <input type="radio" checked={charge} onChange={() => setCharge(true)} />
          Se le cobran
        </label>
        <label style={{ display: "flex", gap: ".3rem", alignItems: "center" }}>
          <input type="radio" checked={!charge} onChange={() => setCharge(false)} />
          Se le exoneran
        </label>
      </div>
      <label style={{ display: "grid", gap: ".25rem" }}>
        <span style={labelStyle}>Motivo {charge ? "(opcional)" : "(obligatorio)"}</span>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            charge
              ? "p.ej. tercer impago consecutivo"
              : "p.ej. el fallo fue nuestro, no se le repercute"
          }
          style={inputStyle}
        />
      </label>
      {err && <ActionError message={err} hint={null} />}
      <FormButtons
        confirmLabel={busy ? "Aplicando…" : "Aplicar"}
        onConfirm={submit}
        onCancel={onClose}
        disabled={!valid || busy}
      />
    </InlineForm>
  );
}

/** Contenedor de los formularios en línea de una fila de recibo. */
function InlineForm({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: ".5rem",
        padding: ".6rem .7rem",
        background: "#fbfcfe",
        border: `1px solid ${COLORS.rowBorder}`,
        borderRadius: ".4rem",
        display: "grid",
        gap: ".5rem",
      }}
    >
      {children}
    </div>
  );
}

function FormButtons({
  confirmLabel,
  onConfirm,
  onCancel,
  disabled,
}: {
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: ".5rem" }}>
      <button
        type="button"
        onClick={onConfirm}
        disabled={disabled}
        style={{ ...buttonStyle, opacity: disabled ? 0.5 : 1 }}
      >
        {confirmLabel}
      </button>
      <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
        Cancelar
      </button>
    </div>
  );
}

function ActionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card style={{ padding: "0.75rem 0.95rem", marginTop: "0.65rem" }}>
      <h3
        style={{
          margin: "0 0 0.4rem",
          fontSize: ".9rem",
          color: COLORS.heading,
        }}
      >
        {title}
      </h3>
      {children}
    </Card>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: ".82rem", color: COLORS.muted }}>{children}</div>
  );
}

/**
 * Error de una acción. `discreet` es para el 503: "este entorno no lo tiene
 * configurado" no es un fallo del operador, así que se pinta en gris y no en
 * rojo de alarma.
 */
function ActionError({
  message,
  hint,
  discreet,
}: {
  message: string;
  hint: string | null;
  discreet?: boolean;
}) {
  return (
    <div
      style={{
        padding: "0.5rem 0.7rem",
        borderRadius: "0.45rem",
        background: discreet ? COLORS.subtle : COLORS.dangerBg,
        color: discreet ? COLORS.muted : COLORS.dangerFg,
        fontSize: ".8rem",
      }}
    >
      <div>{message}</div>
      {hint && (
        <div style={{ marginTop: "0.25rem", opacity: 0.9 }}>{hint}</div>
      )}
    </div>
  );
}
